import React, { useCallback, useMemo, useState } from 'react';
import { MapContainer, Polygon, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Alert, Button, Radio, Space, Typography } from 'antd';
import { ClearOutlined, UndoOutlined } from '@ant-design/icons';
import type { GeoJSONLineString, GeoJSONPolygon } from '../../api/geo';

/**
 * ГДЕ ЭТО НА КАРТЕ — рисование зоны городского уведомления (Заход 4, 04.09.2026).
 *
 * ЗАЧЕМ. У уведомления есть заголовок, текст, срок и район — и до этого захода
 * ни одной колонки геометрии. «Перекрыта улица Дружбы Народов, участок от 1 до
 * 15» жителю ничего не говорит, пока он не увидит, где именно.
 *
 * ⚠️ ВЫВЕСТИ ЛИНИЮ ИЗ ТЕКСТА НЕЛЬЗЯ. У отключений ЖКХ зона СЧИТАЕТСЯ из адресов
 * («улица Железнодорожная, 30–90» → контуры домов), а «участок от поворота до
 * моста» в базе улиц не найдётся. Поэтому здесь рисует человек.
 *
 * ⚠️ РИСОВАНИЕ САМОДЕЛЬНОЕ — КЛИКАМИ, как в «Районах на карте»
 * (`pages/geo/DistrictsMap.tsx`). `react-leaflet-draw` в проект не тянем:
 * второго способа рисовать по карте в админке быть не должно, а этот уже
 * прошёл боевую эксплуатацию на районах.
 *
 * ⚠️ ГЕОМЕТРИЯ НЕОБЯЗАТЕЛЬНА, И ЭТО НОРМА: у актированного дня зоны нет вовсе —
 * «занятия отменены» касается всего города. Пустая карта в приложении хуже
 * отсутствующей, поэтому «не нарисовано» здесь — полноценный исход, а не
 * недоделанная форма.
 */

/** Центр и приближение — те же, что у «Районов на карте»: это один город. */
const AKSAI_CENTER: [number, number] = [51.166, 53.02];
const AKSAI_ZOOM = 14;

/** Линия перекрытия или замкнутая зона. Точка отдельным видом не нужна: */
/** «где авария» админ ставит той же линией из двух точек, а лишний режим — */
/** лишний выбор в форме, которую заполняют в спешке. */
type Shape = 'line' | 'zone';

type Pt = { lat: number; lng: number };

const ClickCatcher: React.FC<{ onPoint: (lat: number, lng: number) => void }> = ({ onPoint }) => {
  useMapEvents({
    click: (e) => onPoint(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

/** Геометрия из базы → точки редактора (порядок GeoJSON — [lon, lat]). */
function pointsOf(geom: GeoJSONLineString | GeoJSONPolygon | null): { shape: Shape; points: Pt[] } {
  if (!geom) return { shape: 'line', points: [] };
  if (geom.type === 'Polygon') {
    const ring = geom.coordinates?.[0] ?? [];
    // Замыкающую точку кольца в редакторе не показываем: её ставит сохранение,
    // иначе при каждой правке кольцо обрастало бы дублями последней точки.
    const open = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;
    return { shape: 'zone', points: open.map((c) => ({ lat: c[1], lng: c[0] })) };
  }
  return { shape: 'line', points: (geom.coordinates ?? []).map((c) => ({ lat: c[1], lng: c[0] })) };
}

export const NoticeGeomEditor: React.FC<{
  value: GeoJSONLineString | GeoJSONPolygon | null;
  onChange: (geom: GeoJSONLineString | GeoJSONPolygon | null) => void;
}> = ({ value, onChange }) => {
  const initial = useMemo(() => pointsOf(value), [value]);
  const [shape, setShape] = useState<Shape>(initial.shape);
  const [points, setPoints] = useState<Pt[]>(initial.points);

  /** Одно место, где точки превращаются в геометрию — и оно же решает, что
   *  недорисованное = «геометрии нет». Линия из одной точки и «зона» из двух
   *  это не фигуры, отдавать их в базу нельзя. */
  const emit = useCallback(
    (next: Pt[], nextShape: Shape) => {
      if (nextShape === 'zone') {
        if (next.length < 3) return onChange(null);
        const ring = next.map((p) => [p.lng, p.lat]);
        ring.push([ring[0][0], ring[0][1]]);
        return onChange({ type: 'Polygon', coordinates: [ring] });
      }
      if (next.length < 2) return onChange(null);
      return onChange({ type: 'LineString', coordinates: next.map((p) => [p.lng, p.lat]) });
    },
    [onChange],
  );

  const addPoint = (lat: number, lng: number) => {
    const next = [...points, { lat, lng }];
    setPoints(next);
    emit(next, shape);
  };

  const undo = () => {
    const next = points.slice(0, -1);
    setPoints(next);
    emit(next, shape);
  };

  const clear = () => {
    setPoints([]);
    onChange(null);
  };

  const switchShape = (s: Shape) => {
    setShape(s);
    emit(points, s);
  };

  const latlngs: [number, number][] = points.map((p) => [p.lat, p.lng]);

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space>
        <Radio.Group
          value={shape}
          onChange={(e) => switchShape(e.target.value as Shape)}
          optionType="button"
          buttonStyle="solid"
          options={[
            { value: 'line', label: 'Линия (перекрытие)' },
            { value: 'zone', label: 'Зона (полигон)' },
          ]}
        />
        <Button size="small" icon={<UndoOutlined />} onClick={undo} disabled={!points.length}>
          Отменить точку
        </Button>
        <Button size="small" icon={<ClearOutlined />} onClick={clear} disabled={!points.length}>
          Очистить
        </Button>
      </Space>

      <div style={{ height: 320, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
        <MapContainer center={AKSAI_CENTER} zoom={AKSAI_ZOOM} style={{ height: '100%', width: '100%' }} doubleClickZoom={false}>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            attribution="© OpenStreetMap · © CARTO"
          />
          <ClickCatcher onPoint={addPoint} />
          {/* Пока точек меньше трёх, зона рисуется линией: показывать «полигон»
              из двух точек — врать формой о том, что уже нарисовано. */}
          {shape === 'zone' && points.length >= 3 ? (
            <Polygon positions={latlngs} pathOptions={{ color: '#fa541c', weight: 2, fillOpacity: 0.25 }} />
          ) : points.length >= 2 ? (
            <Polyline positions={latlngs} pathOptions={{ color: '#fa541c', weight: 4 }} />
          ) : null}
        </MapContainer>
      </div>

      {points.length ? (
        <Typography.Text type="secondary">
          Точек: {points.length}. {shape === 'zone' ? 'Кольцо замкнётся при сохранении.' : 'Кликайте вдоль перекрытого участка.'}
        </Typography.Text>
      ) : (
        <Alert
          type="info"
          showIcon
          message="Можно не рисовать"
          description="У актированного дня зоны нет — он касается всего города. Тогда в приложении карточка покажется без карты."
        />
      )}
    </Space>
  );
};

export default NoticeGeomEditor;
